import React, { useState, useEffect, useMemo, useId } from 'react';
import { Eye, EyeOff, Edit, Trash2, Save, XCircle, UserPlus, Users, Search, Download } from 'lucide-react';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useAuth } from '../../contexts/AuthContext';
import JiraLinkControl, { JiraPerson } from './JiraLinkControl';
import { scrollBehavior } from '../../utils/motion';

// Shared field styles for the redesigned form
const INPUT =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 ' +
  'shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100';
const LABEL = 'mb-1 block text-xs font-medium text-gray-600';

const ROLE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'leader', label: 'Leader' },
  { value: 'admin', label: 'Admin' },
];


const initials = (name?: string) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

// Single-role badge.
const roleBadge = (role?: string): { label: string; cls: string } => {
  if (role === 'admin') return { label: 'Admin', cls: 'bg-red-100 text-red-700' };
  if (role === 'leader') return { label: 'Leader', cls: 'bg-purple-100 text-purple-700' };
  return { label: 'User', cls: 'bg-blue-100 text-blue-700' };
};

export default function CreateUsersTab() {
  const uid = useId();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { user: me, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState({ createUser: false });

  // Route all feedback to the global toast (strip the leading ✅/❌ marker).
  const notify = (msg: string) =>
    showToast(msg.startsWith('❌') ? 'error' : 'success', msg.replace(/^[✅❌]\s*/, ''));

  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    role: 'user',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editUserForm, setEditUserForm] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'user',
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [jiraUsers, setJiraUsers] = useState<any[]>([]);
  const [jiraUserSearch, setJiraUserSearch] = useState('');
  const [prefillSource, setPrefillSource] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    loadUsers();
    loadJiraUsers();
    loadGroups();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await apiService.getAllUsers();
      if (response.data.success) {
        setUsers(response.data.data.users);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadJiraUsers = async () => {
    try {
      const response = await apiService.getJiraUsers();
      if (response.data?.success) {
        setJiraUsers(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load Jira users:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await apiService.getDistinctUserGroups();
      if (response.data?.success) {
        setGroups(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load user groups:', error);
    }
  };

  // Login <-> Jira person link. The people list is the Jira users already loaded for the import panel.
  const jiraPeople: JiraPerson[] = useMemo(() => jiraUsers.filter((u) => u.account_type === 'atlassian'), [jiraUsers]);
  const holders = useMemo(
    () => new Map<string, string>(users.filter((u) => u.jiraAccountId).map((u) => [u.jiraAccountId as string, u.username as string])),
    [users]
  );
  const unlinkedCount = users.filter((u) => !u.jiraAccountId).length;

  const afterLinkChange = async (loginId: number) => {
    await loadUsers();
    // The banner for an unlinked login reads the signed-in user, so refresh it when it is their own.
    if (me?.id === loginId) await refreshUser();
  };

  const changeJiraLink = async (login: any, accountId: string | null) => {
    try {
      const response = await apiService.setJiraLink(login.id, accountId);
      if (!response.data?.success) throw new Error(response.data?.error || 'Could not save the link');
      const who = login.displayName || login.username;
      const person = accountId ? jiraPeople.find((p) => p.account_id === accountId) : null;
      notify(accountId ? `✅ ${who} is now linked to ${person?.display_name || 'that Jira person'}` : `✅ Removed the Jira link from ${who}`);
      await afterLinkChange(login.id);
    } catch (error: any) {
      notify(`❌ ${error.response?.data?.error || error.message}`);
      throw error;
    }
  };

  const matchByEmail = async () => {
    setMatching(true);
    try {
      const response = await apiService.matchLoginsByEmail();
      if (!response.data?.success) throw new Error(response.data?.error || 'Could not match the logins');
      const { linked, checked } = response.data.data as { linked: number; checked: number };
      const left = checked - linked;
      notify(
        linked === 0
          ? '✅ No login matched a Jira person by email. Pick one by hand.'
          : `✅ Linked ${linked} ${linked === 1 ? 'login' : 'logins'} by email.${left > 0 ? ` ${left} still ${left === 1 ? 'needs' : 'need'} a Jira person picked by hand.` : ''}`
      );
      await loadUsers();
      if (me && users.some((u) => u.id === me.id && !u.jiraAccountId)) await refreshUser();
    } catch (error: any) {
      notify(`❌ ${error.response?.data?.error || error.message}`);
    } finally {
      setMatching(false);
    }
  };

  const handlePrefillFromJiraUser = (jiraUser: any) => {
    const email = jiraUser.email_address || '';
    const username = email ? email.split('@')[0] : '';
    setCreateUserForm({
      username,
      email,
      password: '',
      confirmPassword: '',
      full_name: jiraUser.display_name || '',
      role: 'user',
    });
    setPrefillSource(jiraUser.account_id);
    // Scroll create form into view
    document.getElementById('create-user-form-section')?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
  };

  const handleCreateUser = async () => {
    setIsLoading((prev) => ({ ...prev, createUser: true }));

    // Validation
    if (!createUserForm.username || !createUserForm.email || !createUserForm.password || !createUserForm.full_name) {
      notify('❌ All fields are required');
      setIsLoading((prev) => ({ ...prev, createUser: false }));
      return;
    }

    if (createUserForm.password !== createUserForm.confirmPassword) {
      notify('❌ Passwords do not match');
      setIsLoading((prev) => ({ ...prev, createUser: false }));
      return;
    }

    if (createUserForm.password.length < 6) {
      notify('❌ Password must be at least 6 characters long');
      setIsLoading((prev) => ({ ...prev, createUser: false }));
      return;
    }

    try {
      const response = await apiService.createUser({
        username: createUserForm.username,
        email: createUserForm.email,
        password: createUserForm.password,
        full_name: createUserForm.full_name,
        role: createUserForm.role,
      });

      if (response.data.success) {
        notify('✅ User created successfully!');
        setCreateUserForm({
          username: '',
          email: '',
          password: '',
          confirmPassword: '',
          full_name: '',
          role: 'user',
        });
        setPrefillSource(null);
        loadUsers();
      } else {
        notify(`❌ Error: ${response.data.error}`);
      }
    } catch (error: any) {
      notify(`❌ Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoading((prev) => ({ ...prev, createUser: false }));
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setCreateUserForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user.id);
    setEditUserForm({
      username: user.username || '',
      email: user.email || '',
      password: '',
      full_name: user.displayName || '',
      role: user.role || 'user',
    });
  };

  const handleUpdateUser = async (userId: number) => {
    try {
      const updateData: any = {
        username: editUserForm.username,
        email: editUserForm.email,
        full_name: editUserForm.full_name,
        role: editUserForm.role,
      };

      // Only include password if it's provided
      if (editUserForm.password) {
        updateData.password = editUserForm.password;
      }

      const response = await apiService.updateUser(userId, updateData);

      if (response.data.success) {
        notify('✅ User updated successfully!');
        setEditingUser(null);
        loadUsers();
      } else {
        notify(`❌ Error: ${response.data.error}`);
      }
    } catch (error: any) {
      notify(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const who = users.find((u) => u.id === userId);
    const name = who?.displayName || who?.full_name || who?.username;
    const yes = await confirm({
      title: name ? `Delete ${name}?` : 'Delete this user?',
      message: 'The login is removed for good. This cannot be undone.',
      confirmLabel: 'Delete user',
    });
    if (!yes) return;
    try {
      const response = await apiService.deleteUser(userId);

      if (response.data.success) {
        notify('✅ User deleted successfully!');
        loadUsers();
      } else {
        notify(`❌ Error: ${response.data.error}`);
      }
    } catch (error: any) {
      notify(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleEditInputChange = (field: string, value: string | boolean) => {
    setEditUserForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditUserForm({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'user',
    });
  };

  const ALLOWED_GROUPS = ['Business Team', 'Developers', 'Ops and MIS', 'Product Managers', 'QA Team', 'DevOps', 'Designer'];
  const visibleGroups = groups.filter((g) => ALLOWED_GROUPS.includes(g));

  const filteredJiraUsers = jiraUsers.filter((u) => {
    const q = jiraUserSearch.toLowerCase();
    const matchSearch =
      !q ||
      (u.display_name || '').toLowerCase().includes(q) ||
      (u.email_address || '').toLowerCase().includes(q);
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && u.active !== false) ||
      (statusFilter === 'inactive' && u.active === false);
    const matchGroup = !groupFilter || (Array.isArray(u.groups) && u.groups.includes(groupFilter));
    return matchSearch && matchStatus && matchGroup;
  });

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return [u.displayName, u.full_name, u.username, u.email].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">User management</h3>
        <p className="text-sm text-gray-600">Create, update, and manage dashboard user accounts</p>
      </div>

      {/* Top row: Create form + Import from Jira */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* ── Create new user ── */}
        <div id="create-user-form-section" className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <UserPlus className="h-5 w-5" />
            </span>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Create new user</h4>
              <p className="text-xs text-gray-500">Add a dashboard account manually</p>
            </div>
          </div>

          {/* Pre-fill banner */}
          {prefillSource &&
            (() => {
              const src = jiraUsers.find((u) => u.account_id === prefillSource);
              return src ? (
                <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                  <span>
                    Pre-filled from <strong>{src.display_name || src.email_address}</strong>
                  </span>
                  <button
                    onClick={() => {
                      setPrefillSource(null);
                      setCreateUserForm({ username: '', email: '', password: '', confirmPassword: '', full_name: '', role: 'user' });
                    }}
                    aria-label="Clear the pre-filled Jira user"
                    className="ml-2 rounded p-0.5 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null;
            })()}

          <form className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor={`${uid}-1`} className={LABEL}>Full name *</label>
                <input
                  id={`${uid}-1`}
                  type="text"
                  value={createUserForm.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  placeholder="Jane Doe"
                  className={INPUT}
                />
              </div>
              <div>
                <label htmlFor={`${uid}-2`} className={LABEL}>Username *</label>
                <input
                  id={`${uid}-2`}
                  type="text"
                  value={createUserForm.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="jane.doe"
                  className={INPUT}
                />
              </div>
            </div>

            <div>
              <label htmlFor={`${uid}-3`} className={LABEL}>Email address *</label>
              <input
                id={`${uid}-3`}
                type="email"
                value={createUserForm.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="jane@jarvis.consulting"
                className={INPUT}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor={`${uid}-4`} className={LABEL}>Password *</label>
                <div className="relative">
                  <input
                    id={`${uid}-4`}
                    type={showPassword ? 'text' : 'password'}
                    value={createUserForm.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Min. 6 characters"
                    className={`${INPUT} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor={`${uid}-5`} className={LABEL}>Confirm password *</label>
                <div className="relative">
                  <input
                    id={`${uid}-5`}
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={createUserForm.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Re-enter password"
                    className={`${INPUT} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor={`${uid}-6`} className={LABEL}>Role</label>
              <select
                id={`${uid}-6`}
                value={createUserForm.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className={INPUT}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleCreateUser}
              disabled={isLoading.createUser}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <UserPlus className="h-4 w-4" />
              {isLoading.createUser ? 'Creating…' : 'Create User'}
            </button>
          </form>
        </div>

        {/* ── Import from Jira ── */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <Download className="h-5 w-5" />
            </span>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Import from Jira</h4>
              <p className="text-xs text-gray-500">Prefill the form from a synced Jira user</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              aria-label="Search Jira people by name or email"
              placeholder="Search by name or email…"
              value={jiraUserSearch}
              onChange={(e) => setJiraUserSearch(e.target.value)}
              className={`${INPUT} pl-9`}
            />
          </div>

          {/* Status filter */}
          <div className="mb-2 flex items-center gap-2">
            <span className="shrink-0 text-xs font-medium text-gray-500">Status</span>
            <div className="flex gap-1">
              {(['all', 'active', 'inactive'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`hit-area-sm rounded-full border px-2.5 py-0.5 text-xs transition-colors ${statusFilter === s ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-blue-400'}`}
                >
                  {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Group filter chips */}
          <div className="mb-3 flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-xs font-medium text-gray-500">Group</span>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setGroupFilter('')}
                className={`hit-area-sm rounded-full border px-2.5 py-0.5 text-xs transition-colors ${groupFilter === '' ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-400'}`}
              >
                All
              </button>
              {visibleGroups.map((g) => (
                <button
                  key={g}
                  onClick={() => setGroupFilter(g === groupFilter ? '' : g)}
                  className={`hit-area-sm rounded-full border px-2.5 py-0.5 text-xs transition-colors ${groupFilter === g ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-400'}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {jiraUsers.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No users found. Sync users from Jira first.</p>
          ) : filteredJiraUsers.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No users match your filters.</p>
          ) : (
            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {filteredJiraUsers.map((u) => (
                <div
                  key={u.account_id}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2 transition-colors ${prefillSource === u.account_id ? 'border-blue-300 bg-blue-50' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[0.6875rem] font-semibold text-gray-600">
                    {initials(u.display_name || u.email_address)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {u.display_name || u.email_address || u.account_id}
                    </span>
                    {u.email_address && <span className="block truncate text-xs text-gray-500">{u.email_address}</span>}
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ${u.active === false ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                    {u.active === false ? 'Inactive' : 'Active'}
                  </span>
                  <button
                    onClick={() => handlePrefillFromJiraUser(u)}
                    className="shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
                    title="Fill create form with this user" aria-label="Fill create form with this user"
                  >
                    <UserPlus className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Dashboard users (full width) ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span aria-hidden className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <Users className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900">Dashboard users</h4>
            <p className="text-xs text-gray-500">Accounts that can sign in to this dashboard</p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              aria-label="Search dashboard users"
              placeholder="Search users…"
              className={`${INPUT} pl-9`}
            />
          </div>
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">{filteredUsers.length}</span>
        </div>

        {users.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl bg-gray-50 px-3.5 py-2.5 ring-1 ring-gray-200/70">
            <span className={`h-2 w-2 shrink-0 rounded-full ${unlinkedCount > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`} aria-hidden="true" />
            <p className="min-w-0 flex-1 text-sm text-gray-700">
              {unlinkedCount === 0 ? 'Every login is linked to a Jira person.' : `${unlinkedCount} of ${users.length} logins are not linked to a Jira person.`}
              <span className="text-gray-500"> The link decides whose work each login sees.</span>
            </p>
            {unlinkedCount > 0 && (
              <button
                type="button"
                onClick={() => void matchByEmail()}
                disabled={matching}
                className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 ring-1 ring-inset ring-gray-300 transition-[transform,background-color] duration-150 hover:bg-gray-100 active:scale-[0.98] motion-reduce:transform-none disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              >
                {matching ? 'Matching…' : 'Match by email'}
              </button>
            )}
          </div>
        )}

        {users.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No dashboard users found.</p>
        ) : filteredUsers.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No users match your search.</p>
        ) : (
          <div className="grid grid-cols-1 items-start gap-2 md:grid-cols-2">
            {filteredUsers.map((u) => (
              <div key={u.id} className="rounded-xl border border-gray-200">
                {editingUser === u.id ? (
                  /* ── Inline Edit Form ── */
                  <div className="space-y-3 p-3.5">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor={`${uid}-7`} className={LABEL}>Username</label>
                        <input
                          id={`${uid}-7`}
                          type="text"
                          value={editUserForm.username}
                          onChange={(e) => handleEditInputChange('username', e.target.value)}
                          className={INPUT}
                        />
                      </div>
                      <div>
                        <label htmlFor={`${uid}-8`} className={LABEL}>Full name</label>
                        <input
                          id={`${uid}-8`}
                          type="text"
                          value={editUserForm.full_name}
                          onChange={(e) => handleEditInputChange('full_name', e.target.value)}
                          className={INPUT}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor={`${uid}-9`} className={LABEL}>Email</label>
                      <input
                        id={`${uid}-9`}
                        type="email"
                        value={editUserForm.email}
                        onChange={(e) => handleEditInputChange('email', e.target.value)}
                        className={INPUT}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor={`${uid}-10`} className={LABEL}>New password</label>
                        <div className="relative">
                          <input
                            id={`${uid}-10`}
                            type={showEditPassword ? 'text' : 'password'}
                            value={editUserForm.password}
                            onChange={(e) => handleEditInputChange('password', e.target.value)}
                            placeholder="Leave blank to keep"
                            className={`${INPUT} pr-9`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowEditPassword(!showEditPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-500 hover:text-gray-600"
                          >
                            {showEditPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label htmlFor={`${uid}-11`} className={LABEL}>Role</label>
                        <select
                          id={`${uid}-11`}
                          value={editUserForm.role}
                          onChange={(e) => handleEditInputChange('role', e.target.value)}
                          className={INPUT}
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleUpdateUser(u.id)}
                        className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        <Save className="h-3.5 w-3.5" />
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Display Row ── */
                  <div className="flex items-center gap-3 px-3.5 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-xs font-semibold text-white">
                      {initials(u.displayName || u.full_name || u.username)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-gray-900">
                          {u.displayName || u.full_name || u.username}
                        </span>
                        {(() => {
                          const b = roleBadge(u.role);
                          return (
                            <span className={`rounded-full px-1.5 py-0.5 text-[0.6875rem] font-medium ${b.cls}`}>{b.label}</span>
                          );
                        })()}
                      </div>
                      <span className="block truncate text-xs text-gray-500">{u.email}</span>
                      <span className="text-xs text-gray-500">@{u.username}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => handleEditUser(u)}
                        className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
                        title="Edit user" aria-label={`Edit ${u.displayName || u.full_name || u.username}`}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u.id)}
                        className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
                        title="Delete user" aria-label={`Delete ${u.displayName || u.full_name || u.username}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
                {editingUser !== u.id && (
                  <JiraLinkControl login={u} people={jiraPeople} holders={holders} onChange={(accountId) => changeJiraLink(u, accountId)} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
        <h4 className="mb-2 font-medium text-blue-900">ℹ️ User roles</h4>
        <div className="space-y-1 text-sm text-blue-700">
          <p>• <strong>Admin:</strong> Full access — configure Jira settings and manage all users and data</p>
          <p>• <strong>Leader:</strong> Sees all worklog data; no Settings or Worklogs by person pages</p>
          <p>• <strong>User:</strong> Sees data scoped to their assigned team members (and themselves)</p>
        </div>
      </div>
    </div>
  );
}
